import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { Seo } from "@/seo/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { StartBuild } from "@/components/site/primitives";
import { INSIGHTS, INSIGHT_CATEGORIES } from "@/data/insights";
import { STATIC_META, breadcrumbSchema } from "@/seo/config";

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-70px" },
  transition: { duration: 0.7, ease },
};
const CONTAINER = "max-w-[1400px] mx-auto px-6 md:px-12";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const Card = ({ a, featured }) => (
  <motion.article
    {...reveal}
    data-testid={`insight-card-${a.slug}`}
    className={`group relative rounded-2xl border border-[#191919] bg-[#0a0a0a] p-7 md:p-8 hover:border-cyan-400/40 transition-colors duration-300 ${
      featured ? "lg:col-span-2" : ""
    }`}
  >
    <Link to={`/insights/${a.slug}`} className="absolute inset-0" aria-label={a.title} />
    <div className="flex items-center gap-3 mono-label text-cyan-400/80">
      <span>{a.category}</span>
      {a.kind === "pillar" && (
        <span className="rounded-full border border-cyan-400/25 bg-cyan-400/[0.06] px-2.5 py-0.5 text-[10px] text-cyan-300">
          PILLAR GUIDE
        </span>
      )}
    </div>
    <h2
      className={`mt-4 font-heading font-bold tracking-tight text-white ${
        featured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
      }`}
    >
      {a.title}
    </h2>
    <p className="mt-3 text-zinc-400 leading-relaxed">{a.excerpt}</p>
    <div className="mt-6 flex items-center justify-between">
      <div className="flex items-center gap-4 mono-label text-zinc-600">
        <span>{fmtDate(a.publishDate)}</span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" /> {a.readMins} min
        </span>
      </div>
      <span className="inline-flex items-center gap-1.5 mono-label text-zinc-400 group-hover:text-cyan-300 transition-colors">
        Read <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
      </span>
    </div>
  </motion.article>
);

export default function Insights() {
  const [cat, setCat] = useState("All");
  const crumbs = [{ label: "Home", to: "/" }, { label: "Insights", to: "/insights" }];
  const meta = STATIC_META["/insights"];

  const filtered = useMemo(
    () => (cat === "All" ? INSIGHTS : INSIGHTS.filter((a) => a.category === cat)),
    [cat]
  );

  return (
    <div>
      <Seo
        title={meta.title}
        description={meta.description}
        path="/insights"
        schema={[breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />

      <section className="pt-10 md:pt-14 pb-12 md:pb-16">
        <div className={CONTAINER}>
          <motion.div {...reveal} className="mono-label text-cyan-400 mb-6">
            INSIGHTS &amp; GUIDES
          </motion.div>
          <motion.h1
            {...reveal}
            className="font-heading font-extrabold tracking-tighter leading-[0.98] text-4xl md:text-6xl max-w-4xl"
          >
            Practical guidance on building with AI and software.
          </motion.h1>
          <motion.p {...reveal} className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-400 leading-relaxed">
            Original, hype-free guides from the team that ships. Written to help you make better
            decisions about AI transformation, custom software and product strategy.
          </motion.p>
        </div>
      </section>

      <section className="pb-6">
        <div className={CONTAINER}>
          <div className="flex flex-wrap gap-2.5" data-testid="insights-filter-chips">
            {["All", ...INSIGHT_CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCat(c)}
                className={`rounded-full border px-5 py-2 mono-label transition-colors duration-200 ${
                  cat === c
                    ? "border-cyan-400/50 bg-cyan-400/[0.08] text-cyan-300"
                    : "border-[#232323] text-zinc-400 hover:border-[#333] hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className={`${CONTAINER} grid md:grid-cols-2 lg:grid-cols-3 gap-6`}>
          {filtered.map((a, i) => (
            <Card key={a.slug} a={a} featured={cat === "All" && i === 0} />
          ))}
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className={CONTAINER}>
          <div className="rounded-3xl border border-[#161616] bg-[#0a0a0a] p-10 md:p-16 text-center">
            <h2 className="font-serif-ed text-white text-4xl md:text-5xl leading-[1.05]">
              Reading is good. Building is better.
            </h2>
            <p className="mt-5 max-w-xl mx-auto text-zinc-400 text-lg">
              Start a guided product journey. Tell us what you want to build — we'll take it from there.
            </p>
            <div className="mt-9 flex justify-center">
              <StartBuild testid="insights-cta-start-build-btn" size="lg" label="Start Your Build" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
